package com.elan.edubridge.shared.data.datasource

import com.elan.edubridge.shared.domain.model.AcademyApplicationRequest
import com.elan.edubridge.shared.domain.model.AcceptInvitationResult
import com.elan.edubridge.shared.domain.model.AmendAttendanceRequest
import com.elan.edubridge.shared.domain.model.BatchCommandResult
import com.elan.edubridge.shared.domain.model.CalendarEventResult
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.CommandResult
import com.elan.edubridge.shared.domain.model.CommentResult
import com.elan.edubridge.shared.domain.model.ContentReportRequest
import com.elan.edubridge.shared.domain.model.ContentReportResolution
import com.elan.edubridge.shared.domain.model.ContentReportResult
import com.elan.edubridge.shared.domain.model.CreateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.CreateClassRequest
import com.elan.edubridge.shared.domain.model.CreateCommentRequest
import com.elan.edubridge.shared.domain.model.CreateInvitationRequest
import com.elan.edubridge.shared.domain.model.AssignmentAttachmentTicket
import com.elan.edubridge.shared.domain.model.CreateAssignmentRequest
import com.elan.edubridge.shared.domain.model.CreateNoticeRequest
import com.elan.edubridge.shared.domain.model.CreatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.CreatePostAttachmentUploadRequest
import com.elan.edubridge.shared.domain.model.CreatePostRequest
import com.elan.edubridge.shared.domain.model.CreateStudentRequest
import com.elan.edubridge.shared.domain.model.InvitationResult
import com.elan.edubridge.shared.domain.model.InvitationPreviewResult
import com.elan.edubridge.shared.domain.model.JoinCodePreviewResult
import com.elan.edubridge.shared.domain.model.JoinCodeResult
import com.elan.edubridge.shared.domain.model.NoticeResult
import com.elan.edubridge.shared.domain.model.NotificationSummary
import com.elan.edubridge.shared.domain.model.PostAttachmentDownloadTicket
import com.elan.edubridge.shared.domain.model.PostAttachmentUploadTicket
import com.elan.edubridge.shared.domain.model.PostResult
import com.elan.edubridge.shared.domain.model.QrSessionResult
import com.elan.edubridge.shared.domain.model.QrTokenResult
import com.elan.edubridge.shared.domain.model.RecordAttendanceRequest
import com.elan.edubridge.shared.domain.model.ScanRequest
import com.elan.edubridge.shared.domain.model.ScanResult
import com.elan.edubridge.shared.domain.model.SubmitJoinApplicationResult
import com.elan.edubridge.shared.domain.model.UpdateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.UpdateAcademyRequest
import com.elan.edubridge.shared.domain.model.UpdateAssignmentRequest
import com.elan.edubridge.shared.domain.model.UpdateClassRequest
import com.elan.edubridge.shared.domain.model.UpdateNoticeRequest
import com.elan.edubridge.shared.domain.model.UpdatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.UpdatePostRequest
import com.elan.edubridge.shared.domain.model.UpdateStudentRequest

/**
 * 콜러블 포트. 학원 데이터의 모든 쓰기 경로와 소비하지 않는 조회 콜러블이 여기를 지난다.
 * rules가 직접 읽기를 허용한 조회만 [EduConnectDataSource]에 둔다.
 *
 * rules가 `classes`/`students`/`enrollments`/`attendanceEvents`/`qrSessions`를 전부
 * `allow write: if false`로 막으므로 클라이언트가 Firestore에 직접 쓸 방법은 존재하지 않는다.
 * 함수 이름은 `edubridge-<함수명>`으로 나가고 리전은 asia-northeast3다(`FirebaseNaming`).
 *
 * **실패는 예외가 아니라 반환값이다.** 모든 함수가 `*Result`를 돌려준다 —
 * Swift 경계를 넘는 uncaught Kotlin 예외는 복구 지점 없이 프로세스를 죽인다.
 * 구현은 `HttpsError`를 잡아 [com.elan.edubridge.shared.domain.model.CommandFailure]로 매핑하고,
 * 코루틴 취소만 그대로 통과시킨다.
 */
interface AcademyCommandDataSource {

    // ---- 프로비저닝 (§S2) ----

    suspend fun submitAcademyApplication(request: AcademyApplicationRequest): CommandResult

    /** SUPER_ADMIN 전용. 학원 문서 + `members/{uid}` + 미러를 한 트랜잭션으로 만든다. */
    suspend fun approveAcademyApplication(applicationId: String): CommandResult

    /** 학원 ADMIN만 기본 정보를 수정한다. */
    suspend fun updateAcademy(request: UpdateAcademyRequest): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 학원 정보 수정을 지원하지 않아요.",
    )

    suspend fun createClass(request: CreateClassRequest): CommandResult

    /** 해당 학원의 EDITOR 이상이 반 정보를 수정한다. */
    suspend fun updateClass(request: UpdateClassRequest): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 반 정보 수정을 지원하지 않아요.",
    )

    suspend fun createStudent(request: CreateStudentRequest): CommandResult

    /** 보호자 연락처 편집 포함. 호출자는 EDITOR 이상이어야 하고 변경은 감사된다(§2.5). */
    suspend fun updateStudent(request: UpdateStudentRequest): CommandResult

    suspend fun createEnrollment(academyId: String, classId: String, studentId: String): CommandResult

    // ---- 초대 (§9.4) ----

    /**
     * 초대 코드 발급. 선생님 초대는 ADMIN, 학생·보호자 초대는 EDITOR 이상이다.
     *
     * 학생 초대는 대상 학생에 이미 계정이 연결돼 있으면 `FAILED_PRECONDITION`으로 거부된다.
     */
    suspend fun createInvitation(request: CreateInvitationRequest): InvitationResult

    /**
     * 초대 확인. 로그인은 필요하지만 초대를 소비하지 않으며, 호출 시도는 서버 감사 로그에 남는다.
     *
     * 코드를 가진 사람이 확인 화면에서 학원 정보를 본 뒤 명시적으로 수락하도록 하기 위한 경로다.
     * `UNUSABLE`은 존재하지 않음·만료·비정상 상태를 합쳐서 코드 열거를 막고, 다른 사람이 수락한
     * `ALREADY_USED`도 사용자의 신원은 내리지 않는다.
     */
    suspend fun previewInvitation(invitationCode: String): InvitationPreviewResult

    /**
     * 초대 수락. **인증된 계정이 학원 구성원이 되는 유일한 경로다.**
     *
     * 학생 초대를 수락해야 `students/{id}.userId`가 채워지고, 그때부터 [scanAttendance]가
     * 요구하는 전제가 성립한다. 호출자는 로그인만 되어 있으면 되고 기존 멤버십은 필요 없다.
     */
    suspend fun acceptInvitation(invitationCode: String): AcceptInvitationResult

    /** 배정 결과가 멤버십 `assignedClassIds`에 반영된다(§4.5). */
    suspend fun assignTeacherToClass(
        academyId: String,
        classId: String,
        teacherUserId: String,
    ): CommandResult

    /**
     * 관찰자 관계를 끝내는 유일한 경로(§4.4). `guardianLinks`가 클라이언트에게
     * `allow write: if false`라 이것 없이는 정리 트리거가 프로덕션에서 발화할 수 없다.
     *
     * 호출자는 학원 ADMIN **또는 보호자 본인**이다 — 학부모가 학원 허락 없이 관찰을 그만둘 수 있어야 한다.
     * [linkId]는 [com.elan.edubridge.shared.domain.model.GuardianLink.linkId]에서 얻는다.
     *
     * 실패: `UNAUTHENTICATED` / `PERMISSION_DENIED`(둘 다 아님) /
     * `NOT_FOUND`(링크 없음) / `FAILED_PRECONDITION`(이미 해지됨).
     */
    suspend fun revokeGuardianLink(academyId: String, linkId: String): CommandResult

    // ---- 출결 (§S2) ----

    /** 선생님 수동 처리. 항상 `source: MANUAL`로 남아 감사된다(§3.7). */
    suspend fun recordAttendance(request: RecordAttendanceRequest): CommandResult

    /** v4 `출석 처리` / 모바일 `2명 출석 처리`. 부분 성공 의미론은 Q19에서 확정한다. */
    suspend fun recordAttendanceBatch(requests: List<RecordAttendanceRequest>): BatchCommandResult

    /** 정정·취소. 삭제가 아니라 AMEND 이벤트 append다(§3.5). */
    suspend fun amendAttendance(request: AmendAttendanceRequest): CommandResult

    // ---- QR 4종 (§3.3) ----

    /** 스태프 전용(EDITOR 이상). 세션과 첫 토큰을 함께 받는다. */
    suspend fun createQrSession(academyId: String, label: String): QrSessionResult

    /** 키오스크가 `rotationSeconds`마다 호출한다. 실패하면 QR을 감추고 안내를 띄운다(§5.4). */
    suspend fun rotateQrToken(academyId: String, sessionId: String): QrTokenResult

    /**
     * **학생 전용.** 검증 체인 전체가 서버에 있다(§3.4) — 클라이언트 판정은 신뢰하지 않는다.
     * 거부는 [ScanResult.rejection]으로 온다.
     */
    suspend fun scanAttendance(request: ScanRequest): ScanResult

    suspend fun endQrSession(academyId: String, sessionId: String): CommandResult

    // ---- 공지 (`notices.ts`) ----

    /**
     * 공지 발행. EDITOR 이상이며, 커밋 후 학원 전체(+연결된 보호자)에게 푸시를 시도한다.
     *
     * 팬아웃 결과는 [NoticeResult.notified]로 함께 오지만 **성공 여부와 무관하다** —
     * 공지는 이미 발행돼 있으므로 전송 실패로 재시도를 유도하면 중복 발행이 된다.
     */
    suspend fun createNotice(request: CreateNoticeRequest): NoticeResult

    /** 제목·본문 부분 수정. 둘 다 비우면 `INVALID_ARGUMENT`, 툼스톤이면 `FAILED_PRECONDITION`. */
    suspend fun updateNotice(request: UpdateNoticeRequest): CommandResult

    /** 소프트 삭제. 문서는 남고 `status`가 DELETED로 바뀌며 제목·본문이 비워진다. */
    suspend fun deleteNotice(academyId: String, noticeId: String): CommandResult

    // ---- 과제 (`assignments.ts`) ----
    //
    // 기본 구현이 UNAVAILABLE인 것은 위 `updateClass`와 같은 이유다 — 아직 이 화면이 없는
    // 클라이언트(iOS·인메모리 데모)가 한 커밋에서 같이 깨지지 않게 한다.

    /**
     * 과제 등록(EDITOR 이상). 커밋 후 대상(반 또는 학원 전체)에게 푸시를 시도하지만
     * 공지와 같은 이유로 그 결과는 성공 여부와 무관하다.
     */
    suspend fun createAssignment(request: CreateAssignmentRequest): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 과제 등록을 지원하지 않아요.",
    )

    /** 제목·마감일 부분 수정. 둘 다 비우면 `INVALID_ARGUMENT`, 툼스톤이면 `FAILED_PRECONDITION`. */
    suspend fun updateAssignment(request: UpdateAssignmentRequest): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 과제 수정을 지원하지 않아요.",
    )

    /** 소프트 삭제. 제출 결과는 지우지 않는다 — 학생이 이미 한 일이다. */
    suspend fun deleteAssignment(academyId: String, assignmentId: String): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 과제 삭제를 지원하지 않아요.",
    )

    /**
     * 스테이징에 올린 파일을 과제 자료로 승격한다(EDITOR).
     *
     * 파일 자체는 클라이언트가 Storage SDK로 `edubridge/uploads/{uid}/{uploadId}`에 먼저 올리고,
     * 이 콜러블이 **실제 객체의 크기·형식을 서버에서 읽어** 학원 경로로 옮긴다. 그래서 이 요청에
     * 크기·MIME을 담지 않는다 — 담아도 서버가 믿지 않는다.
     */
    suspend fun attachAssignmentUpload(
        academyId: String,
        assignmentId: String,
        uploadId: String,
        fileName: String,
    ): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 과제 자료 첨부를 지원하지 않아요.",
    )

    /** 잘못 올린 자료를 내린다(EDITOR). 기록과 객체가 함께 사라진다. */
    suspend fun removeAssignmentAttachment(
        academyId: String,
        assignmentId: String,
        attachmentId: String,
    ): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 과제 자료 삭제를 지원하지 않아요.",
    )

    /** 자료를 여는 5분짜리 서명 URL. ACTIVE 멤버면 누구나 — 학생·보호자가 여는 경로다. */
    suspend fun getAssignmentAttachmentUrl(
        academyId: String,
        assignmentId: String,
        attachmentId: String,
    ): AssignmentAttachmentTicket = AssignmentAttachmentTicket(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 과제 자료 열기를 지원하지 않아요.",
    )

    // ---- 게시판 (`board.ts`) ----

    suspend fun createPost(request: CreatePostRequest): PostResult

    /** [updateNotice]와 같은 규칙. 호출자는 EDITOR 이상이다. */
    suspend fun updatePost(request: UpdatePostRequest): CommandResult

    /**
     * 소프트 삭제. 제목·본문과 **첨부 목록**이 함께 비워진다 —
     * 서브컬렉션인 댓글은 부모를 지워도 남으므로 툼스톤이 스레드를 계속 가리키게 둔다.
     */
    suspend fun deletePost(academyId: String, postId: String): CommandResult

    /**
     * 댓글 작성. **게시판에서 비스태프에게 열린 유일한 쓰기다** — 권한 등급이 아니라
     * ACTIVE 멤버십만 본다. 삭제된 게시글에는 `FAILED_PRECONDITION`이다.
     */
    suspend fun createComment(request: CreateCommentRequest): CommentResult

    /** 댓글 작성자 본인 또는 EDITOR 이상(모더레이션). 소프트 삭제이며 `commentCount`가 감산된다. */
    suspend fun deleteComment(academyId: String, postId: String, commentId: String): CommandResult

    /**
     * 첨부 업로드 티켓 발급(EDITOR 이상). 게시글에 첨부를 예약하고 10분짜리 서명 PUT URL을 준다.
     *
     * **Storage SDK 업로드는 rules가 전면 차단한다** — 반드시 [PostAttachmentUploadTicket.uploadUrl]에
     * `requiredHeaders`를 그대로 실은 평범한 PUT을 보내야 한다. 서명 실패는 `UNAVAILABLE`,
     * 게시글당 5개 초과는 `FAILED_PRECONDITION`이다.
     */
    suspend fun createPostAttachmentUploadUrl(
        request: CreatePostAttachmentUploadRequest,
    ): PostAttachmentUploadTicket

    /** 첨부 열람용 5분짜리 서명 GET URL. ACTIVE 멤버 누구나 받을 수 있고 매번 새로 발급된다. */
    suspend fun getPostAttachmentUrl(
        academyId: String,
        postId: String,
        attachmentId: String,
    ): PostAttachmentDownloadTicket

    // ---- 신고 (`reports.ts`) ----

    /**
     * 신고 접수. **권한 등급이 아니라 ACTIVE 멤버십만 본다** — [createComment]와 같은 이유로,
     * 신고는 모더레이터가 아니라 구성원이 하는 일이다.
     *
     * 세 콜러블(`reportPost`/`reportComment`/`reportUser`)을 함수 하나로 받는다.
     * [ContentReportRequest.targetType]이 어느 콜러블로 갈지 정하고, 구현이 그 분기를 진다 —
     * 화면 입장에서 셋은 진입점만 다른 같은 동작이라 포트를 셋으로 쪼개면 호출부마다 같은
     * when이 반복된다.
     *
     * **중복 신고는 실패가 아니다.** 신고 문서 id가 `{대상종류}_{대상id}_{신고자uid}`로
     * 결정적이라 같은 사람의 재신고는 같은 행을 가리킨다. 그 경우
     * [ContentReportResult.alreadyReported]가 참으로 오며 `failure`는 null이다 —
     * 화면은 이걸 오류가 아니라 "이미 접수됨"으로 보여야 한다.
     */
    suspend fun reportContent(request: ContentReportRequest): ContentReportResult =
        ContentReportResult(
            failure = CommandFailure.UNAVAILABLE,
            errorMessage = "이 기기에서는 아직 신고를 지원하지 않아요.",
        )

    /**
     * 신고 종결(EDITOR 이상). [ContentReportResolution.ACTIONED]는 "조치했다",
     * [ContentReportResolution.DISMISSED]는 "신고가 타당하지 않았다"로 서로 다른 결말이다.
     *
     * 종결은 영구하지 않다 — 같은 신고자가 같은 대상을 다시 올리면 이 행이 다시 열린다.
     * 한 번의 기각이 그 사람을 그 대상에 대해 영구히 침묵시키지 않게 하기 위한 것이다.
     */
    suspend fun resolveContentReport(
        academyId: String,
        reportId: String,
        resolution: ContentReportResolution,
        note: String?,
    ): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 신고 처리를 지원하지 않아요.",
    )

    // ---- 일정 (`events.ts`) ----

    /**
     * 학원 일정 등록. **EDITOR 이상(학원장·선생님)만** 할 수 있다.
     *
     * 공지와 달리 **푸시를 보내지 않는다** — 캘린더는 한 번에 여러 건을 채우는 화면이라
     * 등록마다 알리면 한 달치 일정이 한 달치 알림이 된다. 그래서 [CalendarEventResult]에는
     * `notified`가 없다.
     */
    suspend fun createAcademyEvent(request: CreateAcademyEventRequest): CalendarEventResult

    /** 부분 수정. 전부 비우면 `INVALID_ARGUMENT`, 툼스톤이면 `FAILED_PRECONDITION`이다. */
    suspend fun updateAcademyEvent(request: UpdateAcademyEventRequest): CommandResult

    /** 소프트 삭제. 문서는 남고 `status`가 DELETED로 바뀌며 제목·설명이 비워진다. */
    suspend fun deleteAcademyEvent(academyId: String, eventId: String): CommandResult

    /**
     * 개인 일정 등록. 로그인만 되어 있으면 되고 학원 멤버십은 필요 없다 —
     * 학원에 속하지 않은 뷰어도 자기 캘린더는 쓴다.
     *
     * 개인 일정도 콜러블을 지나는 이유는 인가 때문이 아니라(경로가 이미 본인만 가리킨다)
     * 시각 정규화·순서 검증·툼스톤 삭제가 여기 있기 때문이다(`events.ts` 머리말).
     */
    suspend fun createPersonalEvent(request: CreatePersonalEventRequest): CalendarEventResult

    /** 본인 일정만. 남의 `eventId`는 권한 실패가 아니라 `NOT_FOUND`로 돌아온다. */
    suspend fun updatePersonalEvent(request: UpdatePersonalEventRequest): CommandResult

    /** 소프트 삭제. 학원 일정과 같은 툼스톤이라 두 캘린더를 같은 필터로 합칠 수 있다. */
    suspend fun deletePersonalEvent(eventId: String): CommandResult

    // ---- W7 참여 코드 + 가입 신청 (`join.ts`) ----
    //
    // 웹이 먼저 붙는다. 미구현 기기는 [updateAcademy]·[reportContent]와 같은 관례로
    // UNAVAILABLE 기본 구현을 상속한다 — 앱이 UI를 붙일 때 abstract로 전환한다
    // (`contentReports`가 밟은 길, a8e7a58).

    /**
     * 참여 코드 발급(ADMIN). **멱등** — ACTIVE 코드가 있으면 새로 만들지 않고 그대로
     * 반환한다([JoinCodeResult.replayed]). 스태프 설정 화면의 "현재 코드 표시"도 이 호출을
     * 쓴다(표시용 호출이 게시판에 붙여 둔 코드를 무효화하면 안 되므로 멱등이 계약이다).
     */
    suspend fun issueAcademyJoinCode(academyId: String): JoinCodeResult = JoinCodeResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 참여 코드를 지원하지 않아요.",
    )

    /** 참여 코드 회전(ADMIN). 기존 ACTIVE 전부 회수 + 신규 발급이 한 트랜잭션이다. */
    suspend fun rotateAcademyJoinCode(academyId: String): JoinCodeResult = JoinCodeResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 참여 코드를 지원하지 않아요.",
    )

    /**
     * 참여 코드 확인(로그인 필수). 비소비이며 시간당 예산을 제출과 **공유**한다 —
     * 한도 초과는 `RESOURCE_EXHAUSTED`로 온다. 정규화(대문자화·공백/하이픈 제거)는
     * 서버가 하므로 입력을 그대로 보내면 된다.
     */
    suspend fun previewAcademyJoinCode(joinCode: String): JoinCodePreviewResult =
        JoinCodePreviewResult(
            failure = CommandFailure.UNAVAILABLE,
            errorMessage = "이 기기에서는 아직 참여 코드를 지원하지 않아요.",
        )

    /**
     * 가입 신청 제출(로그인 필수). [consentAgreed]가 참이 아니면 서버가
     * `INVALID_ARGUMENT`로 거부한다 — 이름·이메일 전달의 법적 근거가 이 체크박스라
     * 기본값으로 채울 수 없는 값이다. 상태기계는 [JoinApplicationStatus] 참조.
     */
    suspend fun submitAcademyJoinApplication(
        joinCode: String,
        consentAgreed: Boolean,
    ): SubmitJoinApplicationResult = SubmitJoinApplicationResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 가입 신청을 지원하지 않아요.",
    )

    /** 본인 PENDING 신청 취소. 취소도 24시간 쿨다운의 기준점이 된다. */
    suspend fun cancelAcademyJoinApplication(academyId: String): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 가입 신청을 지원하지 않아요.",
    )

    /**
     * 가입 신청 승인(EDITOR). 성공 시 [CommandResult.entityId]는 연결·생성된 학생 문서 id다.
     *
     * [studentId]를 지정하면 그 학생 레코드에 계정을 연결한다(출결 이력 보존) — 타 학원
     * 학생은 구조적으로 `NOT_FOUND`, 이미 다른 계정이 연결된 학생은 `FAILED_PRECONDITION`이다.
     * null이면 신청자 이름으로 학생을 새로 만든다. 역할은 서버가 STUDENT로 고정한다.
     */
    suspend fun approveAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
        studentId: String?,
    ): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 가입 신청 처리를 지원하지 않아요.",
    )

    /**
     * 가입 신청 반려(EDITOR). [blockFuture]가 참이면 REJECTED 대신 BLOCKED로 보내
     * 재신청을 막는다 — 차단 사유는 신청자에게 노출되지 않는다.
     */
    suspend fun rejectAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
        blockFuture: Boolean,
    ): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 가입 신청 처리를 지원하지 않아요.",
    )

    /**
     * 차단 해제(EDITOR). BLOCKED를 REJECTED로 내리고 `decidedAt`을 지금으로 갱신한다 —
     * 해제 후에도 24시간 쿨다운을 거쳐 재신청하게 해 큐 복귀 경로를 하나로 유지한다.
     */
    suspend fun unblockAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
    ): CommandResult = CommandResult(
        failure = CommandFailure.UNAVAILABLE,
        errorMessage = "이 기기에서는 아직 가입 신청 처리를 지원하지 않아요.",
    )

    // ---- 알림 (§S3) ----

    /**
     * v4 하단 `전달 71 · 재시도 2`.
     * `notificationDeliveries`가 최상위라 경로에 academyId가 없어 rules로 못 읽는다.
     */
    suspend fun notificationSummary(academyId: String): NotificationSummary
}
